import { useTranslation } from "react-i18next"
import { useCallback, useMemo } from "react"
import { cn } from "@/lib/utils"
import { Combobox } from "@/components/ui/combobox"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { usePagination } from "./search-context"

export function PagingInfo() {
  const { t } = useTranslation("search")
  const { pagingStart, pagingEnd, totalResults } = usePagination()
  return (
    <span className="flex flex-row text-xs">
      {t("result.paging.info", {
        start: pagingStart,
        end: pagingEnd,
        total: totalResults,
      })}
    </span>
  )
}

export function Paging({
  show: visiblePages = 5,
  jump = 5,
}: {
  show: number
  jump: number
}) {
  const { currentPage, totalPages, setCurrentPage } = usePagination()
  const firstPage = 1

  const handlePageChange = useCallback(
    (page: number) => {
      if (currentPage === page) return
      setCurrentPage(page)
    },
    [currentPage, setCurrentPage],
  )

  const handleJumpForward = useCallback(() => {
    handlePageChange(Math.min(currentPage + jump, totalPages))
  }, [currentPage, handlePageChange, jump, totalPages])

  const handleJumpBackward = useCallback(() => {
    handlePageChange(Math.max(currentPage - jump, firstPage))
  }, [currentPage, handlePageChange, jump, firstPage])

  if (totalPages === 0) return null
  const pageRange = getPaginationRange(
    firstPage + 1,
    totalPages - 1,
    currentPage,
    visiblePages,
  )
  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            disabled={currentPage === firstPage}
            onClick={() => handlePageChange(currentPage - 1)}
          />
        </PaginationItem>

        <PaginationItem>
          <PaginationLink
            isActive={currentPage === firstPage}
            onClick={() => handlePageChange(firstPage)}
          >
            {firstPage}
          </PaginationLink>
        </PaginationItem>

        {pageRange[0] > firstPage + 1 ? (
          <PaginationItem>
            <PaginationEllipsis onClick={handleJumpBackward} />
          </PaginationItem>
        ) : null}

        {pageRange.map((page) => (
          <PaginationItem key={page}>
            <PaginationLink
              isActive={currentPage === page}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}

        {totalPages - 1 > pageRange[visiblePages - 1] ? (
          <PaginationItem>
            <PaginationEllipsis onClick={handleJumpForward} />
          </PaginationItem>
        ) : null}

        {totalPages !== firstPage ? (
          <PaginationItem>
            <PaginationLink
              isActive={currentPage === totalPages}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        ) : null}

        <PaginationItem>
          <PaginationNext
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

function getPaginationRange(
  min: number,
  max: number,
  current: number,
  show: number,
): number[] {
  const totalPages = Math.min(show, max - min + 1)

  if (totalPages <= 0) return []

  if (max - min + 1 <= show) {
    return Array.from({ length: max - min + 1 }, (_, i) => min + i)
  }

  let start = current - Math.floor(show / 2)
  let end = current + Math.floor(show / 2)

  if (start < min) {
    const offset = min - start
    start = min
    end = Math.min(max, end + offset)
  }

  if (end > max) {
    const offset = end - max
    end = max
    start = Math.max(min, start - offset)
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

export function ResultsPerPage({
  className,
  range = [20, 40, 60],
}: {
  className?: string
  range?: number[]
}) {
  const { pageSize, setPageSize } = usePagination()
  const defaultPageSize = range[0]

  const handleChange = useCallback(
    (value: number | null) => {
      if (value === null) return
      setPageSize(value)
    },
    [setPageSize],
  )

  const options = useMemo(
    () =>
      range?.map((option) => ({
        label: String(option),
        value: option,
      })),
    [range],
  )

  return (
    <div className="w-min">
      <Combobox
        className={cn("", className)}
        onChange={handleChange}
        options={options}
        value={pageSize ?? defaultPageSize}
        isSearchable={false}
      />
    </div>
  )
}
